import { ChakraV3, dataAttr } from '@piccolohealth/ui';
import React from 'react';
import { FaBars, FaMobileAlt } from 'react-icons/fa';
import { TbLayoutSidebarRightCollapse } from 'react-icons/tb';
import { NavLink, type To, useLocation } from 'react-router-dom';
import { OrganizationChooserButton } from '../../features/organization/OrganizationChooser';
import { useAppContext } from '../../hooks/useAppContext';
import { PiccoloIcon } from '../generic/PiccoloIcon';

export const SidebarDivider = (props: ChakraV3.StackProps) => (
  <ChakraV3.Stack px={4} {...props}>
    <ChakraV3.Separator />
  </ChakraV3.Stack>
);

interface SidebarSectionProps extends ChakraV3.StackProps {
  title?: string;
}

export const SidebarSection = (props: React.PropsWithChildren<SidebarSectionProps>) => {
  const { title, children, ...rest } = props;

  return (
    <ChakraV3.Stack px={4} py={4} {...rest}>
      {title && (
        <ChakraV3.Text fontWeight='bold' fontSize='md'>
          {title}
        </ChakraV3.Text>
      )}
      <ChakraV3.Stack gap={2}>{children}</ChakraV3.Stack>
    </ChakraV3.Stack>
  );
};

export interface SidebarButtonProps extends ChakraV3.ButtonProps {
  isActive?: boolean;
  isShrunk?: boolean;
  leftIcon?: React.ReactNode;
}

export const SidebarButton = React.forwardRef<HTMLButtonElement, SidebarButtonProps>(
  (props, ref) => {
    const { isActive, isShrunk, children, leftIcon, ...rest } = props;

    if (isShrunk) {
      return (
        <ChakraV3.IconButton
          ref={ref}
          variant='ghost'
          aria-label={'sidebar-button'}
          color='gray.600'
          {...{ 'data-active': dataAttr(isActive) }}
          _active={{
            color: 'purple.500',
            bg: 'gray.100',
          }}
          w='full'
          size='sm'
          {...rest}
        >
          {leftIcon}
        </ChakraV3.IconButton>
      );
    }

    return (
      <ChakraV3.Button
        ref={ref}
        variant='ghost'
        justifyContent='start'
        w='full'
        color='gray.600'
        {...{ 'data-active': dataAttr(isActive) }}
        _active={{
          color: 'purple.500',
          bg: 'gray.100',
        }}
        cursor='pointer'
        size='sm'
        fontWeight='semibold'
        fontSize='md'
        {...rest}
      >
        {leftIcon}
        {children}
      </ChakraV3.Button>
    );
  },
);

export interface SidebarButtonLinkProps extends SidebarButtonProps {
  to: To;
}

export const SidebarButtonLink = (props: React.PropsWithChildren<SidebarButtonLinkProps>) => {
  const { to, ...rest } = props;

  return (
    <NavLink to={props.to}>
      {({ isActive }) => <SidebarButton isActive={isActive} {...rest} />}
    </NavLink>
  );
};

interface SidebarHeaderProps {
  isShrunk: boolean;
  onShrink: () => void;
}

const SidebarHeader = (props: SidebarHeaderProps) => {
  const { isShrunk, onShrink } = props;

  if (isShrunk) {
    return (
      <ChakraV3.HStack justify='center' align='center' py={4}>
        <ChakraV3.IconButton
          size='md'
          variant='ghost'
          aria-label={'sidebar-button'}
          color='gray.600'
          onClick={onShrink}
        >
          <TbLayoutSidebarRightCollapse size='20px' />
        </ChakraV3.IconButton>
      </ChakraV3.HStack>
    );
  }

  return <OrganizationChooserButton onShrink={onShrink} />;
};

interface SidebarContentProps extends ChakraV3.StackProps {
  isShrunk: boolean;
  onShrink: () => void;
}

const SidebarContent = (props: SidebarContentProps) => {
  const { isShrunk, onShrink, children, ...rest } = props;

  return (
    <ChakraV3.Stack
      borderColor='gray.200'
      borderRightWidth='1px'
      h='full'
      flexShrink={0}
      gap={0}
      w={{ base: 'full', md: isShrunk ? 'auto' : '220px' }}
      {...rest}
    >
      <SidebarHeader isShrunk={isShrunk} onShrink={onShrink} />
      {children}
    </ChakraV3.Stack>
  );
};

interface MobileNavProps extends ChakraV3.StackProps {
  onOpen: () => void;
}

const MobileNav = ({ onOpen, ...rest }: MobileNavProps) => {
  const { organization } = useAppContext();

  return (
    <ChakraV3.HStack
      flexShrink={0}
      ml={{ base: 0, md: 60 }}
      px={{ base: 4, md: 24 }}
      py={4}
      borderBottomWidth='1px'
      {...rest}
    >
      {organization.logo ? (
        <ChakraV3.Avatar
          src={organization.logo ?? undefined}
          w='42px'
          h='42px'
          p='2px'
          color='purple.600'
          shape='rounded'
          fontWeight='bold'
          name={organization.name}
          bg='transparent'
        />
      ) : (
        <PiccoloIcon boxSize='6' color='purple.500' />
      )}
      <ChakraV3.Text pt={1} fontSize={16} fontWeight='bold'>
        {organization.name}
      </ChakraV3.Text>
      <ChakraV3.Spacer />

      <NavLink to='/recording'>
        <ChakraV3.Button colorPalette='brand'>
          <FaMobileAlt />
          Mobile data collection
        </ChakraV3.Button>
      </NavLink>
      <ChakraV3.IconButton variant='outline' onClick={onOpen} aria-label='open menu'>
        <FaBars />
      </ChakraV3.IconButton>
    </ChakraV3.HStack>
  );
};

interface SidebarLayoutProps {
  width?: string;
  sidebarContent: (props: { isShrunk: boolean }) => React.ReactNode;
  mainContent: React.ReactNode;
}

export const SidebarLayout = (props: SidebarLayoutProps) => {
  const { sidebarContent, mainContent } = props;

  const desktopSidebar = ChakraV3.useDisclosure();
  const mobileSidebar = ChakraV3.useDisclosure();

  const location = useLocation();
  const { onClose } = mobileSidebar;

  // https://github.com/biomejs/biome/issues/630
  // biome-ignore lint/correctness/useExhaustiveDependencies: We want to close the sidebar when the location changes
  React.useEffect(() => {
    onClose();
  }, [location, onClose]);

  return (
    <ChakraV3.Stack h='full'>
      <ChakraV3.Stack hideFrom='md'>
        <MobileNav onOpen={mobileSidebar.onOpen} />

        <ChakraV3.Drawer.Root
          onOpenChange={mobileSidebar.onClose}
          open={mobileSidebar.open}
          placement='start'
          size='full'
        >
          <ChakraV3.Drawer.Backdrop />
          <ChakraV3.Drawer.Positioner>
            <ChakraV3.Drawer.Content>
              <SidebarContent display='flex' onShrink={mobileSidebar.onToggle} isShrunk={false}>
                {sidebarContent({ isShrunk: false })}
              </SidebarContent>
            </ChakraV3.Drawer.Content>
          </ChakraV3.Drawer.Positioner>
        </ChakraV3.Drawer.Root>
      </ChakraV3.Stack>
      <ChakraV3.HStack align='stretch' h='full' minH={0}>
        <ChakraV3.Stack hideBelow='md'>
          <SidebarContent
            onShrink={desktopSidebar.onToggle}
            isShrunk={desktopSidebar.open}
            display='flex'
          >
            {sidebarContent({ isShrunk: desktopSidebar.open })}
          </SidebarContent>
        </ChakraV3.Stack>
        <ChakraV3.Stack h='full' w='full' overflowY='auto' bg='white'>
          {mainContent}
        </ChakraV3.Stack>
      </ChakraV3.HStack>
    </ChakraV3.Stack>
  );
};
